import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';

/**
 * Data-fetching and mutation logic for the Transformers Competition admin page.
 *
 * Owns the single competition record (there is only ever one season), its
 * players/rounds/standings/scheduled-weeks/weekly-recap, the eligible-member
 * count, and every season/week/player mutation handler. Mutation helpers
 * mirror the original component's behavior of reloading the full snapshot
 * (`loadAll`) after a successful write, and they throw on failure so the
 * page can decide the toast copy per action.
 */
export function useCompetitionAdmin() {
  const [competition, setCompetition] = useState(null);
  const [players, setPlayers] = useState([]);
  const [standings, setStandings] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [eligibleCount, setEligibleCount] = useState(null);
  const [scheduledWeeks, setScheduledWeeks] = useState([]);
  const [weeklyRecap, setWeeklyRecap] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const compsRes = await apiService.getCompetitions();
      const theCompetition = (compsRes.data || [])[0] || null;
      setCompetition(theCompetition);

      if (!theCompetition) {
        setPlayers([]);
        setRounds([]);
        setStandings(null);
        setScheduledWeeks([]);
        return;
      }

      const playersRes = await apiService.getCompetitionPlayers(theCompetition.id);
      setPlayers(playersRes.data || []);

      const roundsRes = await apiService.getCompetitionRounds(theCompetition.id);
      setRounds(roundsRes.data || []);

      const scheduleRes = await apiService.getCompetitionScheduledWeeks(theCompetition.id);
      setScheduledWeeks(scheduleRes.data || []);

      const lastClosedRound = (roundsRes.data || [])
        .filter(r => r.status === 'completed')
        .sort((a, b) => b.round_number - a.round_number)[0];
      if (lastClosedRound) {
        apiService.getCompetitionRoundRecap(theCompetition.id, lastClosedRound.id)
          .then(res => setWeeklyRecap(res.data))
          .catch(() => setWeeklyRecap(null));
      } else {
        setWeeklyRecap(null);
      }

      if (theCompetition.current_round_id) {
        const standingsRes = await apiService.getCompetitionRoundStandings(theCompetition.id, theCompetition.current_round_id);
        setStandings(standingsRes.data);
      } else {
        setStandings(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load competition');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    apiService.getUsers().then(res => {
      const withSternUsername = (res.data || []).filter(u => u.stern_username);
      setEligibleCount(withSternUsername.length);
    }).catch(() => {});
  }, [loadAll]);

  const createCompetition = useCallback(async (name, description) => {
    await apiService.createCompetition({ name, description });
    await loadAll();
  }, [loadAll]);

  const startSeason = useCallback(async (competitionId, { sternEventCode, machineAverageScore }) => {
    await apiService.startCompetitionSeason(competitionId, {
      sternEventCode: sternEventCode || undefined,
      machineAverageScore
    });
    await loadAll();
  }, [loadAll]);

  const saveSchedule = useCallback(async (competitionId, codes, startWeek) => {
    const res = await apiService.setCompetitionScheduledWeeks(competitionId, codes, startWeek);
    setScheduledWeeks(res.data || []);
    return res.data;
  }, []);

  const removeScheduledWeek = useCallback(async (competitionId, roundNumber) => {
    await apiService.deleteCompetitionScheduledWeek(competitionId, roundNumber);
    setScheduledWeeks(prev => prev.filter(w => w.roundNumber !== roundNumber));
  }, []);

  const saveWeekEdit = useCallback(async (competitionId, weekNumber, code) => {
    // Reuses the bulk endpoint with every other slot left blank - the backend skips blanks, so
    // this only touches the one week being corrected.
    const codes = [];
    codes[weekNumber - 1] = code;
    const res = await apiService.setCompetitionScheduledWeeks(competitionId, codes);
    setScheduledWeeks(res.data || []);
    return res.data;
  }, []);

  const saveAverage = useCallback(async (competitionId, roundId, average) => {
    await apiService.updateCompetitionRoundAverage(competitionId, roundId, average);
    await loadAll();
  }, [loadAll]);

  const deleteCompetition = useCallback(async (competitionId) => {
    await apiService.deleteCompetition(competitionId);
    await loadAll();
  }, [loadAll]);

  const syncRound = useCallback(async (competitionId, roundId) => {
    await apiService.syncCompetitionRound(competitionId, roundId);
    await loadAll();
  }, [loadAll]);

  const closeWeek = useCallback(async (competitionId, roundId, nextSternEventCode) => {
    const result = await apiService.closeCompetitionWeek(competitionId, roundId, {
      nextSternEventCode: nextSternEventCode || null
    });
    await loadAll();
    return result.data;
  }, [loadAll]);

  const saveOverride = useCallback(async (competitionId, playerKey, updates) => {
    await apiService.updateCompetitionPlayer(competitionId, playerKey, updates);
    await loadAll();
  }, [loadAll]);

  const removePlayer = useCallback(async (competitionId, playerKey) => {
    await apiService.removeCompetitionPlayer(competitionId, playerKey);
    await loadAll();
  }, [loadAll]);

  const resolveTie = useCallback(async (competitionId, payload) => {
    await apiService.declareCompetitionGrandChampion(competitionId, payload);
    await loadAll();
  }, [loadAll]);

  return {
    competition,
    players,
    standings,
    rounds,
    loading,
    error,
    eligibleCount,
    scheduledWeeks,
    weeklyRecap,
    loadAll,
    createCompetition,
    startSeason,
    saveSchedule,
    removeScheduledWeek,
    saveWeekEdit,
    saveAverage,
    deleteCompetition,
    syncRound,
    closeWeek,
    saveOverride,
    removePlayer,
    resolveTie
  };
}

export default useCompetitionAdmin;
